import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../../auth/useAuth.ts';
import { describeError } from '../../ui/problemDetails.ts';
import { groupKeys } from '../groups/groupsApi.ts';
import { acceptInvitation, invitationKeys, previewInvitation } from './invitationsApi.ts';

/**
 * Where an emailed accept link lands.
 *
 * Handles all three arrivals: signed in as the right account (accept immediately),
 * signed out (say what the invitation is, then offer to sign in or sign up), and signed
 * in as the wrong account (say so plainly — the server refuses, and "something went
 * wrong" would leave someone stuck).
 */
export function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [error, setError] = useState<string | null>(null);
  const attempted = useRef(false);

  const { data: preview, isPending: previewPending } = useQuery({
    queryKey: ['invitation-preview', token],
    queryFn: () => previewInvitation(token!),
    enabled: Boolean(token),
    retry: false,
  });

  useEffect(() => {
    if (!token || !isAuthenticated || attempted.current) {
      return;
    }
    attempted.current = true;

    void (async () => {
      try {
        await acceptInvitation({ token });
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: groupKeys.all }),
          queryClient.invalidateQueries({ queryKey: invitationKeys.mine }),
        ]);
        await navigate('/', { replace: true });
      } catch (caught) {
        setError(describeError(caught));
      }
    })();
  }, [token, isAuthenticated, navigate, queryClient]);

  if (!token) {
    return (
      <Shell>
        <h2>That link is incomplete</h2>
        <p className="muted">
          Copy the whole address from the email, including everything after the question mark.
        </p>
        <Link className="button button--quiet" to="/">
          Go to the app
        </Link>
      </Shell>
    );
  }

  if (isLoading || previewPending) {
    return (
      <Shell>
        <p className="muted">Loading…</p>
      </Shell>
    );
  }

  if (!preview?.isUsable) {
    return (
      <Shell>
        <h2>This invitation is no longer valid</h2>
        <p className="muted">
          It may have expired or been withdrawn. Ask whoever invited you to send a new one.
        </p>
        <Link className="button button--quiet" to="/">
          Go to the app
        </Link>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <h2>Couldn&rsquo;t accept this invitation</h2>
        <p className="form-error" role="alert">
          {error}
        </p>
        <p className="muted">
          It was sent to <strong>{preview.invitedEmail}</strong>.
        </p>
        <Link className="button button--quiet" to="/">
          Go to the app
        </Link>
      </Shell>
    );
  }

  if (isAuthenticated) {
    return (
      <Shell>
        <p className="muted">Joining {preview.groupName}…</p>
      </Shell>
    );
  }

  return (
    <Shell>
      <h2>
        {preview.invitedByDisplayName} invited you to {preview.groupName}
      </h2>
      <p className="muted">
        Sign in as <strong>{preview.invitedEmail}</strong> to accept.
      </p>

      {preview.accountExists ? (
        <Link className="button button--primary" to="/login">
          Sign in
        </Link>
      ) : (
        <Link
          className="button button--primary"
          to={`/register?invitation=${encodeURIComponent(token)}`}
        >
          Create an account
        </Link>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="app-shell app-shell--centred">
      <section className="card stack">{children}</section>
    </main>
  );
}
